import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { churchInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Phone, Mail, Eye, EyeOff, AlertTriangle, CheckCircle, KeyRound } from 'lucide-react';
import api from '../services/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('email');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetStep, setResetStep] = useState('verify'); // 'verify' or 'newPassword'
  const [resetEmail, setResetEmail] = useState('');
  const [resetPhone, setResetPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [phoneForm, setPhoneForm] = useState({ phone: '', pin: '' });
  const [emailForm, setEmailForm] = useState({ email: '', password: '' });

  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { phone: phoneForm.phone, pin: phoneForm.pin });
      const data = response.data;
      
      if (data.requiresReset) {
        setFailedAttempts(data.failedAttempts);
        setResetPhone(phoneForm.phone);
        setShowResetDialog(true);
        setResetStep('newPassword');
      } else if (data.token) {
        // Successful login
        localStorage.setItem('smbc_token', data.token);
        localStorage.setItem('smbc_user', JSON.stringify(data.user));
        window.location.href = '/home';
      } else if (data.message) {
        setError(data.message);
        setFailedAttempts(data.failedAttempts);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid phone number or PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email: emailForm.email, password: emailForm.password });
      const data = response.data;
      
      if (data.requiresReset) {
        setFailedAttempts(data.failedAttempts);
        setResetEmail(emailForm.email);
        setShowResetDialog(true);
        setResetStep('newPassword');
      } else if (data.token) {
        // Successful login
        localStorage.setItem('smbc_token', data.token);
        localStorage.setItem('smbc_user', JSON.stringify(data.user));
        window.location.href = '/home';
      } else if (data.message) {
        setError(data.message);
        setFailedAttempts(data.failedAttempts);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }
    
    setResetting(true);
    setError('');
    
    try {
      const resetData = {
        newPassword: newPassword
      };
      
      if (resetEmail) {
        resetData.email = resetEmail;
      } else if (resetPhone) {
        resetData.phone = resetPhone;
      }
      
      await api.post('/auth/reset-password', resetData);
      setResetSuccess(true);
      
      // Auto close after success
      setTimeout(() => {
        setShowResetDialog(false);
        setResetSuccess(false);
        setResetStep('verify');
        setNewPassword('');
        setConfirmPassword('');
        setFailedAttempts(0);
        setError('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openForgotPassword = () => {
    setResetEmail(emailForm.email || '');
    setResetPhone(phoneForm.phone || '');
    setResetStep('verify');
    setShowResetDialog(true);
  };

  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    // Just move to password step - verification happens on submit
    setResetStep('newPassword');
  };

  return (
    <div className="min-h-screen bg-navy-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Church Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-24 h-24 rounded-full bg-white p-1 shadow-lg mb-4">
            <img
              src={churchInfo.logo}
              alt={churchInfo.name}
              className="w-full h-full object-contain rounded-full"
            />
          </div>
          <h1 className="text-xl font-bold text-white text-center">
            Second Missionary Baptist Church
          </h1>
          <p className="text-tan-400 text-sm italic mt-1">
            Where God gets ALL the Glory
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          {error && !showResetDialog && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Tab Buttons */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('phone')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'phone'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Phone className="w-4 h-4" />
              Phone + PIN
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'email'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
          </div>

          {activeTab === 'phone' ? (
            <form onSubmit={handlePhoneLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-navy-900 font-medium">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(559) 555-1234"
                  className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                  value={phoneForm.phone}
                  onChange={(e) => setPhoneForm({ ...phoneForm, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pin" className="text-navy-900 font-medium">PIN (4-6 digits)</Label>
                <div className="relative">
                  <Input
                    id="pin"
                    type={showPin ? 'text' : 'password'}
                    placeholder="Enter your PIN"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-10"
                    value={phoneForm.pin}
                    onChange={(e) => setPhoneForm({ ...phoneForm, pin: e.target.value })}
                    required
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white font-medium rounded-lg text-base"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <button
                type="button"
                onClick={openForgotPassword}
                className="w-full text-sm text-tan-600 hover:text-tan-700 font-medium"
              >
                Forgot PIN?
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-navy-900 font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-navy-900 font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-10"
                    value={emailForm.password}
                    onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white font-medium rounded-lg text-base"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <button
                type="button"
                onClick={openForgotPassword}
                className="w-full text-sm text-tan-600 hover:text-tan-700 font-medium"
              >
                Forgot Password?
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-tan-600 hover:text-tan-700 font-medium">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Password/PIN Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-tan-600" />
              Reset {activeTab === 'phone' ? 'PIN' : 'Password'}
            </DialogTitle>
          </DialogHeader>

          {resetSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-bold text-gray-900">{activeTab === 'phone' ? 'PIN' : 'Password'} Reset!</p>
              <p className="text-sm text-gray-500 mt-2">You can now login with your new {activeTab === 'phone' ? 'PIN' : 'password'}.</p>
            </div>
          ) : resetStep === 'verify' ? (
            <form onSubmit={handleVerifyIdentity} className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter your email or phone number to verify your identity.
              </p>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              {activeTab === 'email' ? (
                <div className="space-y-2">
                  <Label className="text-navy-900 font-medium">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="you@email.com"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-navy-900 font-medium">Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="(559) 555-1234"
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                    value={resetPhone}
                    onChange={(e) => setResetPhone(e.target.value)}
                    required
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  onClick={() => setShowResetDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl"
                >
                  Continue
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {failedAttempts >= 3 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg text-sm">
                  Too many failed attempts. Please create a new password.
                </div>
              )}
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-navy-900 font-medium">
                  New {activeTab === 'phone' ? 'PIN (4-6 digits)' : 'Password'}
                </Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder={activeTab === 'phone' ? 'Enter new PIN' : 'Enter new password'}
                    className="h-11 bg-gray-50 border-gray-200 rounded-lg pr-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(activeTab === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value)}
                    required
                    minLength={4}
                    maxLength={activeTab === 'phone' ? 6 : undefined}
                    inputMode={activeTab === 'phone' ? 'numeric' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-navy-900 font-medium">
                  Confirm {activeTab === 'phone' ? 'PIN' : 'Password'}
                </Label>
                <Input
                  type="password"
                  placeholder={activeTab === 'phone' ? 'Confirm new PIN' : 'Confirm new password'}
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(activeTab === 'phone' ? e.target.value.replace(/\D/g, '') : e.target.value)}
                  required
                  maxLength={activeTab === 'phone' ? 6 : undefined}
                  inputMode={activeTab === 'phone' ? 'numeric' : undefined}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  onClick={() => {
                    setShowResetDialog(false);
                    setResetStep('verify');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl"
                  disabled={resetting}
                >
                  {resetting ? 'Resetting...' : `Reset ${activeTab === 'phone' ? 'PIN' : 'Password'}`}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
